import { useId, useSyncExternalStore, useCallback, useContext } from "react";
import { debounce } from "../../other/utils/otherUtils.jsx";
import { ProjectModel, Store } from "@bryntum/schedulerpro";
import BryntumContext from "../context/bryntumContext.jsx";
import { getOriginalRecord } from "@/other/utils/utils.jsx";

type TActionType = 'remove' | 'add' | 'update';

const SUPPORTED_ACTIONS: TActionType[] = ['remove', 'add', 'update'];

/**
 * Payload passed to subscriber callbacks (only emitted for `add` | `update` | `remove` after debounce).
 * @template T - The type of record in the store
 */
interface TStoreChangeArgs<T> {
  record: T;
  records: T[];
  action: TActionType;
  changes: Record<string, unknown>;
  source: Store;
}

/**
 * Optional subscriber function that receives store change notifications.
 * Return true to trigger a re-render, false to skip it.
 * @template T - The type of record in the store
 */
type TSubscribeFn<T> = ((args: TStoreChangeArgs<T>) => boolean | void) | undefined;

/**
 * Global store for Bryntum store listeners (one listener per store ID)
 * This implements a singleton pattern to avoid multiple listeners on the same store
 */
const storeListenerStore = new Map<string, {
  detacher: () => void;
}>();

/**
 * Global store for React component subscribers (one entry per hook instance)
 * Each entry tracks which store it's subscribed to and maintains a version counter
 */
const subscriberStore = new Map<string, {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  subscribers: Set<TSubscribeFn<any>>;
  storeId: string;
  version: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reactSubscriberFn: any;
}>();

/**
 * Debounce delay in milliseconds for batching store change notifications
 * Set to 16ms (~1 frame at 60fps) to batch rapid consecutive changes
 */
const DEBOUNCE_DELAY = 16;

/**
 * Cleans up all subscribers and listeners for a destroyed store
 * Called when a Bryntum store is destroyed via beforeDestroy event
 * @param storeId - The ID of the store being destroyed
 */
const cleanupDestroyedStore = (storeId: string) => {
  const storeListener = storeListenerStore.get(storeId);
  if (storeListener) {
    storeListener.detacher();
    storeListenerStore.delete(storeId);
  }

  // Clean up all subscribers for this store
  const subscribersToRemove = Array.from(subscriberStore.entries())
    .filter(([_, subscriber]) => subscriber.storeId === storeId)
    .map(([hookId, _]) => hookId);

  subscribersToRemove.forEach(hookId => {
    subscriberStore.delete(hookId);
  });
};

/**
 * Notifies all React components subscribed to a store when it changes
 * Components can opt-in/opt-out of re-renders via their subscriberFn
 * @template T - The type of record in the store
 * @param args - Store change event arguments from Bryntum
 */
const notifySubscribers = <T>(args: TStoreChangeArgs<T>) => {
  const { source: store } = args;
  // Find all hooks subscribed to this specific store
  const subscribers = Array.from(subscriberStore.values()).filter(subscriber => subscriber.storeId === store.id);

  for (const subscriber of subscribers) {
    // Call all subscriber functions and check if any return true (want re-render)
    const subscriberWantsRerender = Array.from(subscriber.subscribers.values())
      .map(subscriberFn => subscriberFn?.(args))
      .some(result => result);

    const noSubscribers = subscriber.subscribers.size === 0;
    // Re-render if: 1) Any subscriber function returned true, OR 2) No subscriber functions (always re-render)
    if (subscriberWantsRerender || noSubscribers) {
      subscriber.version++;
      subscriber.reactSubscriberFn?.();
    }
  }
}

/**
 * React hook for subscribing to Bryntum store changes with optimized re-rendering
 *
 * **Architecture:**
 * - Singleton listener: one `change` handler per `storeId`, shared by all hook instances
 * - **Only `add`, `update`, and `remove`** are forwarded to React. Other Bryntum `change` actions
 *   (`filter`, `dataset`, `replace`, `clearChanges`, `removeAll`, …) are **ignored** here, so
 *   `version` and subscribers **do not** run for those events
 * - Each of the three actions uses its **own** 16ms debouncer so e.g. a burst of `update` does not
 *   cancel a pending `remove` (unlike a single shared debouncer)
 * - Before notify: `record` is set from `args.record || args.records[0]` when dispatching
 * - Selective re-renders via optional `subscriberFn`; listeners detach when the last subscriber unmounts
 *
 * **Usage Examples:**
 *
 * ```typescript
 * // Basic usage - re-render on any store change
 * const { version } = useSwivuStore({ storeId: STORE_KEYS.taskStore });
 *
 * // Selective re-rendering - only re-render for specific records
 * const { version } = useSwivuStore<Task>({
 *   storeId: STORE_KEYS.taskStore,
 *   subscriberFn: ({ record }) => record.id === currentTaskId
 * });
 *
 * // Dynamic subscriptions - add additional filters later
 * const { version, subscribe } = useSwivuStore({ storeId: STORE_KEYS.taskStore });
 * useEffect(() => {
 *   return subscribe(({ action }) => action === 'add');
 * }, [subscribe]);
 * ```
 *
 * **Performance / behavior notes:**
 * - Debounce window is 16ms per action channel
 * - With no `subscriberFn`, the hook re-renders when **any** of the three actions fires for this store
 *   (after debounce), not on every Bryntum `change`
 * - `subscriberFn`: return `true` to request a re-render, `false` / omitted to skip
 *
 * @template T - The type of record stored in the Bryntum store
 * @param storeId - The ID of the Bryntum store to subscribe to (from project.getCrudStore)
 * @param subscriberFn - Optional filter function to selectively trigger re-renders
 * @returns Object with version number (increments on changes) and subscribe function for dynamic subscriptions
 */
export const useSwivuStore = <T>({ storeId, subscriberFn }: {storeId: string, subscriberFn?: TSubscribeFn<T>}) => {
  const { project } = useContext(BryntumContext);
  const projectInstance = project as unknown as ProjectModel | undefined;
  const hookId = useId();

  // Validate inputs but throw errors after all hooks are defined to avoid hook order issues
  const isValid = Boolean(storeId) && Boolean(projectInstance);

  /**
   * Dynamically add a subscriber function to filter store changes
   * Useful for conditional subscriptions based on component state
   * @param subscriberFn - Function that returns true to re-render, false to skip
   * @returns Cleanup function to remove the subscriber
   */
  const subscribe = useCallback((subscriberFn: TSubscribeFn<T>): () => void => {
    if (!isValid) return () => {};

    const subscriber = subscriberStore.get(hookId);
    if (subscriber && !subscriber.subscribers.has(subscriberFn)) {
      subscriber.subscribers.add(subscriberFn);
      return () => {
        subscriber.subscribers.delete(subscriberFn);
      }
    }
    return () => {};
  }, [hookId, isValid])


  const forceRerender = useCallback(() => {
    const subscriber = subscriberStore.get(hookId);
    if (subscriber) {
      subscriber.version++;
      subscriber.reactSubscriberFn?.();
    }
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const internalSubscribeFn = useCallback((onStoreChange: any) => {
    if (!isValid || !projectInstance) return () => {};
    const storeListener = storeListenerStore.get(storeId);

    // Singleton pattern: If no listener exists for this store, create one
    // This ensures only ONE event listener per store, shared across all hook instances
    if (!storeListener) {
      const debouncedNotifier = Object.fromEntries(SUPPORTED_ACTIONS.map(action => [action, debounce(notifySubscribers<T>, DEBOUNCE_DELAY)])) as Record<TActionType, (args: TStoreChangeArgs<T>) => void>;
      const store = projectInstance.getCrudStore(storeId);

      // Validate that the store exists before attaching listeners
      if (!store) {
        console.warn(`[useSwivuStore] Store with id "${storeId}" not found in project`);
        return () => {};
      }

      // Listen to store change events and debounce notifications
      const changeDetacher = store.on("change", (args: TStoreChangeArgs<T>) => {
        const { action } = args;
        if (action in debouncedNotifier) {
          debouncedNotifier[action]({ ...args, record: getOriginalRecord(args.record || args.records[0]) });
        }
      });

      // Handle store destruction to prevent memory leaks
      const destroyDetacher = store.on("beforeDestroy", () => {
        cleanupDestroyedStore(storeId);
      });

      storeListenerStore.set(storeId, {
        detacher: () => {
          changeDetacher();
          destroyDetacher();
        }
      });
    }

    // Register this hook instance as a subscriber
    const subscriber = subscriberStore.get(hookId);
    if (subscriber) {
      // Update the React subscriber function for existing subscriber
      subscriber.reactSubscriberFn = onStoreChange;
    } else {
      // Create new subscriber entry for this hook instance
      const subscribers = new Set<TSubscribeFn<T>>();
      if (subscriberFn) subscribers.add(subscriberFn);
      const subscriber = { subscribers, storeId, version: 0, reactSubscriberFn: onStoreChange }
      subscriberStore.set(hookId, subscriber);
    }

    // Cleanup function called when component unmounts
    return () => {
      // Remove this hook from subscriber maps
      subscriberStore.delete(hookId);

      // Check if there are any remaining subscribers for this store
      const remainingSubscribers = Array.from(subscriberStore.values())
        .filter(subscriber => subscriber.storeId === storeId);

      // If no subscribers remain, cleanup the store listener to prevent memory leaks
      if (remainingSubscribers.length === 0) {
        const storeListener = storeListenerStore.get(storeId);
        if (storeListener) {
          storeListener.detacher();
          storeListenerStore.delete(storeId);
        }
      }
    };
  }, [hookId, storeId, isValid, projectInstance, subscriberFn]);

  const getSnapshot = useCallback(() => subscriberStore.get(hookId)?.version || 0, [hookId]);

  const version = useSyncExternalStore(internalSubscribeFn, getSnapshot);

  return { version, subscribe, forceRerender };
}
